import type { Asset, Draft, Surface } from '../shared/types';
import {clipSurfaceCanvas} from './surfaceCanvasClip';
import { drawDrawingComposite, renderDrawingShadow, type DrawingShadow } from './drawingShadow';
import { sameDrawingPixels } from './brush';
import { hasAuthoredWallChange } from './achievementPixels';

export type ImageFrameStyle = 'none' | 'simpleBorder' | 'posterEdge' | 'pictureFrame';
export interface PreparedSourceEffects { shadow: DrawingShadow; frame: ImageFrameStyle }
export interface SourceLayerRequest {
  context: string; asset: Pick<Asset, 'id' | 'url'>; surface: Surface; draft: Draft;
  width: number; height: number; effects: PreparedSourceEffects;
}
export interface PreparedSourceComposite {
  base: HTMLCanvasElement; revision: number; pixels: ImageData;
  changed: boolean; authored: boolean; estimatedInk: number;
}
export interface PreparedSourceLayer {
  key: string; layer: HTMLCanvasElement; composite?: PreparedSourceComposite;
}

/** Private editor work, independent of the public immutable artwork byte cache. */
export function sourceLayerKey(request: SourceLayerRequest): string {
  const {context,asset,surface,draft,width,height,effects}=request;
  return JSON.stringify([context,asset.id,asset.url,surface,draft,width,height,effects]);
}

export function loadPreparedSourceImage(url:string,signal?:AbortSignal):Promise<HTMLImageElement> {
  return new Promise((resolve,reject)=>{
    const image=new Image();
    // Match same-origin chooser/SVG requests so concurrent private image loads
    // share browser work. Cross-origin pixel reads still require CORS.
    if(new URL(url,window.location.href).origin!==window.location.origin)image.crossOrigin='anonymous';
    const cleanup=()=>{image.onload=null;image.onerror=null;signal?.removeEventListener('abort',abort);};
    const abort=()=>{cleanup();image.src='';reject(signal?.reason??new DOMException('Canceled','AbortError'));};
    image.onload=()=>{cleanup();resolve(image);};
    image.onerror=()=>{cleanup();reject(new Error('This artwork could not be loaded. Your drawing is safe.'));};
    signal?.addEventListener('abort',abort,{once:true});
    if(signal?.aborted){abort();return;}
    image.src=url;
  });
}

/** One selected decoded source and one current layer, never a growing raster cache. */
export class CurrentPreparedSource {
  private selection='';
  private source: {promise:Promise<CanvasImageSource>;controller:AbortController}|null=null;
  private requestedKey:string|null=null;
  private current:PreparedSourceLayer|null=null;
  constructor(private load:(url:string,signal:AbortSignal)=>Promise<CanvasImageSource>=loadPreparedSourceImage,
    private render:typeof renderPreparedSourceLayer=renderPreparedSourceLayer) {}
  select(context:string,asset:Pick<Asset,'id'|'url'>|null) {
    const key=asset?JSON.stringify([context,asset.id,asset.url]):'';
    if(key===this.selection)return;
    this.clear();this.selection=key;
  }
  clearLayer() {
    this.requestedKey=null;
    if(this.current){
      this.current.layer.width=0;this.current.layer.height=0;this.current.composite=undefined;this.current=null;
    }
  }
  clear() {this.clearLayer();this.source?.controller.abort();this.source=null;this.selection='';}
  async prepare(request:SourceLayerRequest):Promise<PreparedSourceLayer> {
    this.select(request.context,request.asset);
    const key=sourceLayerKey(request);this.requestedKey=key;
    if(this.current?.key===key)return this.current;
    if(!this.source){const controller=new AbortController();this.source={controller,promise:this.load(request.asset.url,controller.signal)};}
    const source=this.source;
    let image:CanvasImageSource;
    try{image=await source.promise;}catch(error){if(this.source===source)this.source=null;throw error;}
    source.controller.signal.throwIfAborted();
    if(this.requestedKey!==key)throw new DOMException('Source preparation changed.','AbortError');
    // Another reader may have completed the same source while we were waiting.
    if(this.current?.key===key)return this.current;
    // Presentation owns a canvas copy, so releasing this raster never blanks
    // the displayed source before its replacement is ready.
    this.clearLayer();this.requestedKey=key;
    this.current={key,layer:this.render(image,request.surface,request.draft,request.width,request.height,request.effects)};
    return this.current;
  }
  preview(prepared:PreparedSourceLayer):HTMLCanvasElement {
    if(this.current!==prepared||this.requestedKey!==prepared.key)throw new DOMException('Source preparation changed.','AbortError');
    return prepared.layer;
  }
}

/** Reuse the exact preview composition only while the underlying draft pixels match. */
export function composePreparedSource(prepared:PreparedSourceLayer,base:HTMLCanvasElement,revision:number,estimate:(canvas:HTMLCanvasElement)=>number,
  baseSnapshot?:Pick<PreparedSourceComposite,'base'|'revision'|'pixels'>|null,
  createCanvas:()=>HTMLCanvasElement=()=>document.createElement('canvas')):PreparedSourceComposite {
  const retained=prepared.composite;
  if(retained?.base===base&&retained.revision===revision&&retained.pixels.width===base.width&&retained.pixels.height===base.height)return retained;
  const combined=createCanvas();combined.width=base.width;combined.height=base.height;
  try{
    const context=combined.getContext('2d',{willReadFrequently:true});
    if(!context)throw new Error('Artwork could not be prepared. Your drawing is safe.');
    context.drawImage(base,0,0);
    const before=baseSnapshot?.base===base&&baseSnapshot.revision===revision&&baseSnapshot.pixels.width===base.width&&baseSnapshot.pixels.height===base.height
      ?baseSnapshot.pixels:context.getImageData(0,0,combined.width,combined.height);
    context.drawImage(prepared.layer,0,0);
    const pixels=context.getImageData(0,0,combined.width,combined.height);
    return prepared.composite={base,revision,pixels,changed:!sameDrawingPixels(before.data,pixels.data),authored:hasAuthoredWallChange(before.data,pixels.data),estimatedInk:estimate(combined)};
  }finally{combined.width=0;combined.height=0;}
}
export const IMAGE_FRAME_CHOICES = [
  ['none', 'None'], ['simpleBorder', 'Simple border'], ['posterEdge', 'Poster edge'], ['pictureFrame', 'Picture frame'],
] as const;
interface FrameBand { x:number; y:number; width:number; height:number; color:string }

function ring(width:number,height:number,left:number,top:number,right:number,bottom:number,color:string):FrameBand[] {
  return [
    {x:-width/2-left,y:-height/2-top,width:width+left+right,height:top,color},
    {x:-width/2-left,y:height/2,width:width+left+right,height:bottom,color},
    {x:-width/2-left,y:-height/2,width:left,height,color},
    {x:width/2,y:-height/2,width:right,height,color},
  ];
}

/** Bands sit outside the unchanged source rectangle; transparent source pixels stay clear. */
export function imageFrameBands(width:number,height:number,style:ImageFrameStyle):FrameBand[] {
  if(style==='none')return [];
  const edge=Math.min(width,height);
  if(style==='simpleBorder')return ring(width,height,edge*.018,edge*.018,edge*.018,edge*.018,'#263d36');
  if(style==='posterEdge')return ring(width,height,edge*.055,edge*.055,edge*.055,edge*.11,'#f7f0de');
  const outer=edge*.055,inner=edge*.012;
  return [...ring(width,height,outer,outer,outer,outer,'#72563e'),...ring(width,height,inner,inner,inner,inner,'#d3b989')];
}

/** The same private clipped raster drives placement preview, estimate and the draft bake. */
export function renderPreparedSourceLayer(image:CanvasImageSource,surface:Surface,draft:Draft,width:number,height:number,effects:PreparedSourceEffects,createCanvas:()=>HTMLCanvasElement=()=>document.createElement('canvas')):HTMLCanvasElement {
  const source=createCanvas();source.width=width;source.height=height;
  const context=source.getContext('2d');
  if(!context)throw new Error('Artwork could not be prepared. Your drawing is safe.');
  context.save();context.scale(width/surface.width,height/surface.height);
  clipSurfaceCanvas(context,surface);
  context.translate(draft.x+draft.width/2,draft.y+draft.height/2);context.rotate((draft.rotation??0)*Math.PI/180);
  for(const band of imageFrameBands(draft.width,draft.height,effects.frame)){context.fillStyle=band.color;context.fillRect(band.x,band.y,band.width,band.height);}
  context.drawImage(image,-draft.width/2,-draft.height/2,draft.width,draft.height);context.restore();
  // Frame bands overlap at their seams. Compose native alpha first, then fade
  // the whole contribution once so a 40% frame cannot build up to 64% there.
  let artwork=source;
  const opacity=draft.opacity??1;
  if(opacity!==1){
    artwork=createCanvas();artwork.width=width;artwork.height=height;
    const faded=artwork.getContext('2d');
    if(!faded)throw new Error('Artwork opacity could not be prepared. Your drawing is safe.');
    faded.globalAlpha=opacity;faded.drawImage(source,0,0);
  }
  if(effects.shadow.style==='off')return artwork;
  const shadow=createCanvas();renderDrawingShadow(artwork,shadow,effects.shadow,surface);
  const result=createCanvas();result.width=width;result.height=height;
  const composite=result.getContext('2d');
  if(!composite)throw new Error('Artwork effects could not be prepared. Your drawing is safe.');
  drawDrawingComposite(composite,artwork,shadow,width,height);
  return result;
}
